import { select } from '@ps/web-core-store';
import { Icon } from '@ps/web-fusion-components';
import type { TournamentState } from '@ps/web-poker-tile-mappers';
import classNames from 'clsx';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import FavoriteIcon from '@swf/core/assets/icons/favorite.svg';
import { useWidget } from '@swf/core/hooks/useWidget';
import type { Dispatch } from '@swf/core/store';
import type { PokerMultiTableTournamentsWidgetModelBase } from '@swf/poker';

import { ModalPath } from '../../../types/common';
import { getTournamentStorage, updateFavorites } from '../../../utils/tournamentStorage';
import PriorityImage from '../../PriorityImage/PriorityImage';
import type { Tag } from '../TournamentInfo/Tags/Tags';
import Tags from '../TournamentInfo/Tags/Tags';

import NameBar from './NameBar/NameBar';
import State from './State/State';
import styles from './TournamentCard.scss';

export interface TournamentCardProps {
  state: TournamentState;
  tournamentName?: string;
  isTournamentFavorite?: boolean;
  tags?: Tag[];
  imageUrl: string;
  fallbackImageUrl: string;
  className?: string;
  internalReference?: string;
  favReference?: string;
  handleRemoveFavorites?: () => void;
  widgetId?: string;
}

const TournamentCard: React.FC<TournamentCardProps> = ({
  state,
  tournamentName,
  tags,
  imageUrl,
  fallbackImageUrl,
  className = '',
  internalReference = '',
  favReference = '',
  isTournamentFavorite = false,
  handleRemoveFavorites,
  widgetId
}) => {
  const { modal } = useDispatch<Dispatch>();
  const isMobile = useSelector(select.device.isMobile);
  const { widgetId: desktopWidgetId } = useWidget<
    never,
    PokerMultiTableTournamentsWidgetModelBase
  >();

  const tournamentStorage = getTournamentStorage();
  const dontShowRemoveMessage = tournamentStorage?.dontShowRemoveDialog ?? false;
  const shouldSkipRemoveDialog = isTournamentFavorite && dontShowRemoveMessage;

  const removeTournamentFavorite = () => {
    if (handleRemoveFavorites && (internalReference || favReference)) {
      handleRemoveFavorites();

      const currentSimilar = tournamentStorage?.similarFavorites ?? [];
      const currentSpecific = tournamentStorage?.specificFavorites ?? [];

      const updatedSimilar = currentSimilar.filter((ref: string) => ref !== favReference);
      const updatedSpecific = currentSpecific.filter((ref: string) => ref !== internalReference);

      updateFavorites(updatedSimilar, updatedSpecific);
    }
  };

  const openAddToFavoritesModal = () => {
    modal.unshift([
      {
        path: ModalPath.TournamentFavorites,
        locationState: {
          widgetId: widgetId || desktopWidgetId,
          favReference,
          internalReference,
          isTournamentFavorite
        }
      }
    ]);
  };

  return (
    <div className={classNames(styles.root, className)}>
      <State state={state} className={styles.state} />
      <PriorityImage
        imageUrl={imageUrl}
        fallbackUrl={fallbackImageUrl}
        altText={tournamentName || ''}
        fetchPriority="high"
        className={styles.image}
      />
      {!!tournamentName && <NameBar tournamentName={tournamentName} className={styles.nameBar} />}
      {!!tags && <Tags tags={tags} className={styles.tags} />}
      <Icon
        icon={FavoriteIcon}
        onClick={shouldSkipRemoveDialog ? removeTournamentFavorite : openAddToFavoritesModal}
        className={classNames(styles.addToFavorites, {
          [styles.addToFavoritesActive]: isTournamentFavorite,
          [styles.addToFavoritesHover]: isTournamentFavorite && !isMobile
        })}
      />
    </div>
  );
};

export default TournamentCard;
